import { Injectable } from '@angular/core'
import {
  HttpService,
  IExportFileResponse,
  INew_TableAction,
  ITimePeriodRequest,
  ITimePeriodWithLocationIds,
  TableActionsService
} from '@lla-platform/core/core-data-access'
import { Observable } from 'rxjs'
import { ICallReportResponse } from '../interfaces/calls-report.interface'
import { REPORTS_API_URL } from './reports-api-routes'
import { DateService } from '@lla-platform/core/core-util'
import { ISalesReportResponse } from '../interfaces/sales-report.interface'
import { ICompareReportRequest, ICompareReportResponse } from '../interfaces/compare-report.interface'
import { ObjectiveCategory } from '../enums/objective-category.enum'
import { IObjectivesRankingReportResponse } from '../interfaces/objective-rankings.interface'
import {
  IArMovingAverageReportResponse,
  IArMovingAverageRequest
} from '../interfaces/ar-moving-average.interface'
import { ICategoryMovingAverageReportResponse } from '../interfaces/category-moving-average.interface'
import { IEmployeeEfficiencyRankingReportResponse } from '../interfaces/employee-efficiency-rankings.interface'
import { EmployeeEfficiencyType } from '../enums/employee-efficiency-type.enum'
import { IUpsellReportResponse, IUpsellRequest } from '../interfaces/upsell.interface'
import { IUpsellListResponse } from '../interfaces/upsell-list.interface'
import { IDailyAverageRequest, IDailyAverageResponse } from '../interfaces/daily-average.interface'
import { ICompareObjectivesByGroup } from '../interfaces/compare-objectives-by-group.interface'
import { ICompareObjectivesByRegional } from '../interfaces/compare-objectives-by-regional.interface'
import { IAdjustedGPRequest, IAdjustedGPResponse } from '../interfaces/adjusted-gross-profit.interface'
import { ITiresReportResponse } from '../interfaces/tires-report.interface'

@Injectable({
  providedIn: 'root'
})
export class ReportsService {
  constructor(
    private http: HttpService,
    private dateService: DateService,
    private tableActionsService: TableActionsService
  ) {}

  getCallsReport(payload: ITimePeriodRequest): Observable<ICallReportResponse> {
    const { from, to } = payload
    return this.http.post(REPORTS_API_URL.callsReport, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getSalesReport(payload: ITimePeriodWithLocationIds): Observable<ISalesReportResponse> {
    const { from, to } = payload
    return this.http.post(REPORTS_API_URL.salesReport, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getTiresReport(payload: ITimePeriodWithLocationIds): Observable<ITiresReportResponse> {
    const { from, to } = payload
    return this.http.post(REPORTS_API_URL.tiresReport, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportTiresReport(payload: ITimePeriodWithLocationIds): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(REPORTS_API_URL.exportTiresReport, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getCompareReport(payload: ICompareReportRequest): Observable<ICompareReportResponse> {
    return this.http.post(REPORTS_API_URL.compareReport, payload)
  }

  getWeeklyObjectivesRankingReport(
    objectiveCategory: ObjectiveCategory
  ): Observable<IObjectivesRankingReportResponse> {
    return this.http.get(REPORTS_API_URL.weeklyObjectiveRankings, {
      params: {
        objectiveCategory
      }
    })
  }

  exportWeeklyObjectiveRankings(objectiveCategory: ObjectiveCategory): Observable<IExportFileResponse> {
    return this.http.get(REPORTS_API_URL.exportWeeklyObjectiveRankings, {
      params: {
        objectiveCategory
      }
    })
  }

  getMonthlyObjectivesRankingReport(
    objectiveCategory: ObjectiveCategory
  ): Observable<IObjectivesRankingReportResponse> {
    return this.http.get(REPORTS_API_URL.monthlyObjectiveRankings, {
      params: {
        objectiveCategory
      }
    })
  }

  exportMonthlyObjectiveRankings(objectiveCategory: ObjectiveCategory): Observable<IExportFileResponse> {
    return this.http.get(REPORTS_API_URL.exportMonthlyObjectiveRankings, {
      params: {
        objectiveCategory
      }
    })
  }

  getWeeklyArMovingAverageReport(
    payload: IArMovingAverageRequest
  ): Observable<IArMovingAverageReportResponse> {
    return this.http.post(REPORTS_API_URL.weeklyArMovingAverage, payload)
  }

  exportWeeklyArMovingAverage(payload?: IArMovingAverageRequest): Observable<IExportFileResponse> {
    return this.http.post(REPORTS_API_URL.exportWeeklyArMovingAverage, payload)
  }

  getMonthlyArMovingAverageReport(
    payload: IArMovingAverageRequest
  ): Observable<IArMovingAverageReportResponse> {
    return this.http.post(REPORTS_API_URL.monthlyArMovingAverage, payload)
  }

  exportMonthlyArMovingAverage(payload?: IArMovingAverageRequest): Observable<IExportFileResponse> {
    return this.http.post(REPORTS_API_URL.exportMonthlyArMovingAverage, payload)
  }

  getWeeklyCategoryMovingAverageReport(
    serviceCategoryId: string
  ): Observable<ICategoryMovingAverageReportResponse> {
    return this.http.get(REPORTS_API_URL.weeklyCategoryMovingAverage, {
      params: {
        serviceCategoryId
      }
    })
  }

  getMonthlyCategoryMovingAverageReport(
    serviceCategoryId: string
  ): Observable<ICategoryMovingAverageReportResponse> {
    return this.http.get(REPORTS_API_URL.monthlyCategoryMovingAverage, {
      params: {
        serviceCategoryId
      }
    })
  }

  getWeeklyEmployeeEfficiencyRankingReport(
    employeeEfficiencyType: EmployeeEfficiencyType
  ): Observable<IEmployeeEfficiencyRankingReportResponse> {
    return this.http.get(REPORTS_API_URL.weeklyEmployeeEfficiencyRankings, {
      params: {
        employeeEfficiencyType
      }
    })
  }

  getMonthlyEmployeeEfficiencyRankingReport(
    employeeEfficiencyType: EmployeeEfficiencyType
  ): Observable<IEmployeeEfficiencyRankingReportResponse> {
    return this.http.get(REPORTS_API_URL.monthlyEmployeeEfficiencyRankings, {
      params: {
        employeeEfficiencyType
      }
    })
  }

  // Upsell API calls
  getUpsellReport(payload: IUpsellRequest): Observable<IUpsellReportResponse> {
    return this.http.post(REPORTS_API_URL.upsellReport, payload)
  }

  getUpsellList(info: INew_TableAction): Observable<IUpsellListResponse> {
    return this.http.post(
      REPORTS_API_URL.upsellList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportUpsellList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      REPORTS_API_URL.exportUpsellList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  // Daily Average API calls
  getDailyAverageReport(payload: IDailyAverageRequest): Observable<IDailyAverageResponse> {
    const { startDate, endDate } = payload
    return this.http.post(REPORTS_API_URL.dailyAverageReport, {
      ...payload,
      startDate: this.dateService.momentToDateString(startDate),
      endDate: this.dateService.momentToDateString(endDate)
    })
  }

  exportDailyAverageReport(payload: IDailyAverageRequest): Observable<IExportFileResponse> {
    const { startDate, endDate } = payload
    return this.http.post(REPORTS_API_URL.exportDailyAverageReport, {
      ...payload,
      startDate: this.dateService.momentToDateString(startDate),
      endDate: this.dateService.momentToDateString(endDate)
    })
  }

  // Compare Objectives By Group
  getCompareObjectivesByGroup(
    numberOfMonths: number,
    locationIds: string[] = []
  ): Observable<ICompareObjectivesByGroup> {
    return this.http.post(REPORTS_API_URL.compareObjectivesByGroup, {
      numberOfMonths,
      locationIds
    })
  }

  exportCompareObjectivesByGroup(
    categories: ObjectiveCategory[],
    numberOfMonths: number,
    locationIds: string[] = []
  ): Observable<IExportFileResponse> {
    return this.http.post(REPORTS_API_URL.exportCompareObjectivesByGroup, {
      categories,
      numberOfMonths,
      locationIds
    })
  }

  // Compare Objectives By Regional
  getCompareObjectivesByRegional(
    numberOfMonths: number,
    locationIds: string[] = []
  ): Observable<ICompareObjectivesByRegional> {
    return this.http.post(REPORTS_API_URL.compareObjectivesByRegional, {
      numberOfMonths,
      locationIds
    })
  }

  exportCompareObjectivesByRegional(
    categories: ObjectiveCategory[],
    numberOfMonths: number,
    locationIds: string[] = []
  ): Observable<IExportFileResponse> {
    return this.http.post(REPORTS_API_URL.exportCompareObjectivesByRegional, {
      categories,
      numberOfMonths,
      locationIds
    })
  }

  // Adjusted Gross Profit
  getAdjustedGP(payload: IAdjustedGPRequest): Observable<IAdjustedGPResponse> {
    return this.http.post(REPORTS_API_URL.getAdjustedGP, payload)
  }

  exportAdjustedGP(payload: IAdjustedGPRequest): Observable<IExportFileResponse> {
    return this.http.post(REPORTS_API_URL.exportAdjustedGP, payload)
  }
}
